/**
 * useEditAutosave — Jun-2026 (User-Wunsch P0).
 *
 * Speichert im Bearbeiten-/Disposition-Modus ALLE editierbaren Inserats-Felder
 * automatisch (debounced), sobald der Nutzer etwas ändert. Behebt den Daten-
 * verlust, bei dem z. B. der Zustand (Gebrauchtfahrzeug → Vorführfahrzeug) nur
 * beim manuellen Klick auf „Speichern" gesichert wurde.
 *
 * Vorgehen (analog zur Disposition-Autospeicherung):
 *  - Erst wenn das Inserat geladen ist (`ready`), wird ein Baseline-Snapshot
 *    aufgenommen (kein Rück-Schreiben der gerade geladenen Werte).
 *  - Bei Änderungen werden nur die geänderten Felder gesammelt und nach 800 ms
 *    per PATCH /listings/{id}/autosave gesendet (Backend-Whitelist = ListingCreate).
 *  - Beim Unmount werden ausstehende Änderungen sofort geflusht.
 *
 * Nicht-persistente Meta-Felder (id, owner_id, status, …) werden ausgelassen;
 * das Backend ignoriert ohnehin alles, was nicht zu ListingCreate gehört.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";

// Felder, die NICHT per Autospeicherung überschrieben werden dürfen (Meta /
// serverseitig verwaltet). Alles andere wird vom Backend ohnehin gefiltert.
const BLOCKLIST = new Set([
    "id", "owner_id", "owner", "status", "internal_number", "created_at",
    "updated_at", "mileage_updated_at", "_id", "views", "favorites_count",
    "callback_leads", "sold", "reserved_by", "reserved_until",
    "papers_inventory_code", "papers_inventory_last_counted_at",
]);

function diffKeys(curr, prev) {
    const changed = {};
    for (const k of Object.keys(curr)) {
        if (BLOCKLIST.has(k)) continue;
        if (JSON.stringify(curr[k]) !== JSON.stringify(prev[k])) {
            changed[k] = curr[k] === undefined ? null : curr[k];
        }
    }
    return changed;
}

export default function useEditAutosave({ form, isEdit, ready, listingId }) {
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
    const prevSnapRef = useRef(null);
    const pendingRef = useRef({});
    const timerRef = useRef(null);
    const savedTimerRef = useRef(null);

    const flush = useCallback(async () => {
        const payload = pendingRef.current;
        pendingRef.current = {};
        if (!listingId || Object.keys(payload).length === 0) return;
        setSaveState("saving");
        try {
            await api.patch(`/listings/${listingId}/autosave`, payload);
            setSaveState("saved");
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => setSaveState("idle"), 1800);
        } catch {
            setSaveState("idle");
        }
    }, [listingId]);

    useEffect(() => {
        if (!isEdit || !ready || !listingId || !form) return;
        // Baseline-Snapshot beim ersten „ready"-Render — kein Save.
        if (prevSnapRef.current === null) {
            prevSnapRef.current = { ...form };
            return;
        }
        const changed = diffKeys(form, prevSnapRef.current);
        prevSnapRef.current = { ...form };
        if (Object.keys(changed).length === 0) return;
        Object.assign(pendingRef.current, changed);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, 800);
    }, [form, isEdit, ready, listingId, flush]);

    // Ausstehende Änderungen beim Verlassen sofort sichern.
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        flush();
    }, [flush]);

    return { saveState };
}
